import { marketBaseOf } from "@local/i18n/src/languages"

/**
 * Market-exclusive nav items, keyed by the STABLE slug of whatever the item
 * links to: a listing slug for `listing` items, a collection slug for
 * `collection` items. Value is the base market the item is allowed in.
 *
 * DEV-9850 originally expressed this rule as `item.name !== "Kids"`, matching the
 * item's DISPLAY LABEL. That broke the moment the label was translated: DEV-9806
 * gives the backend per-market nav-label overrides that REPLACE `name` at read
 * time (`channelService.ts`, `name: ctx.navLabelMap.get(item.id) ?? item.name`),
 * so a published German override turned the label into "Kinder", the comparison
 * stopped matching, and a US-only product reappeared in the German navbar.
 *
 * Slugs are the right key because they are the one identifier translation cannot
 * touch: `listingTranslationUpsert` may edit `shortName`, `description`, and
 * `headline`, and `collectionTranslationUpsert` may edit `name` and
 * `description`. Neither can edit a slug. A label is content; a slug is
 * identity. Never gate visibility on content.
 *
 * This is a stopgap. The rule belongs in backend data, not in a storefront
 * constant: the nav blob is channel-global, `channel_nav_override` carries a
 * `label` column and no visibility flag, and nothing in the nav payload reports
 * stock or per-market availability. Until the backend can express "this nav item
 * does not apply to this market", every rule of this kind has to be hardcoded
 * here and is invisible to whoever manages the navigation.
 */
const MARKET_EXCLUSIVE_NAV_SLUGS: Readonly<Record<string, string>> = {
  // "Kids" / "Kinder": a `listing` nav item pointing at BÆRSkin Tactical Hoodie
  // Kids 340gsm. US-only product, out of stock everywhere else.
  "baerskin-kids-4-0": "us",
}

/**
 * Legacy labels for the same items, kept as a safety net for payloads that carry
 * no slug (the static fallback header, or a nav item whose target was unlinked).
 * Matching either the slug or the English label can only widen the hit rate, and
 * the failure mode is "correctly hidden" rather than "wrongly shown".
 */
const MARKET_EXCLUSIVE_NAV_LABELS: Readonly<Record<string, string>> = {
  Kids: "us",
}

interface FilterableNavItem {
  name: string
  slug?: string
}

/**
 * Whether a nav item is restricted to a market other than the one being viewed.
 *
 * @param item - The nav item, already label-resolved by the backend
 * @param marketSlug - The visitor's market slug; may be a public language variant
 *   ("de-en"), which is normalized to its base market because these rules are
 *   about market identity, not language
 */
const isHiddenInMarket = (
  item: FilterableNavItem,
  marketSlug: string
): boolean => {
  const allowedMarket =
    (item.slug ? MARKET_EXCLUSIVE_NAV_SLUGS[item.slug] : undefined) ??
    MARKET_EXCLUSIVE_NAV_LABELS[item.name]

  if (!allowedMarket) return false
  return marketBaseOf(marketSlug) !== allowedMarket
}

/**
 * Drops nav items that do not belong in this market.
 *
 * Top level only, matching the behaviour this replaces. No current rule targets
 * a sub-item. Returns the input array unchanged (same reference) when nothing is
 * filtered, so callers that memoize on identity do not see a false change.
 *
 * @param items - The resolved `navigationMain` array
 * @param marketSlug - The visitor's market slug, base or language variant
 */
export const visibleNavItems = <T extends FilterableNavItem>(
  items: T[] | undefined,
  marketSlug: string
): T[] | undefined => {
  if (!items?.length) return items
  const visible = items.filter((item) => !isHiddenInMarket(item, marketSlug))
  return visible.length === items.length ? items : visible
}
